import { motion } from "framer-motion"
import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/shared/ui/button"

interface PostMediaFullscreenProps {
	imageUrl: string
	onClose: () => void
}

export function PostMediaFullscreen({ imageUrl, onClose }: PostMediaFullscreenProps) {
	const [zoom, setZoom] = useState(1)
	const [pan, setPan] = useState({ x: 0, y: 0 })
	const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
	const pinchRef = useRef<{ startDistance: number; startZoom: number } | null>(null)
	const dragRef = useRef<{
		startX: number
		startY: number
		startPanX: number
		startPanY: number
	} | null>(null)
	const closeButtonRef = useRef<HTMLButtonElement>(null)
	const previouslyFocusedRef = useRef<Element | null>(null)

	useEffect(() => {
		setZoom(1)
		setPan({ x: 0, y: 0 })
		pointersRef.current.clear()
		pinchRef.current = null
		dragRef.current = null
	}, [])

	useEffect(() => {
		previouslyFocusedRef.current = document.activeElement
		closeButtonRef.current?.focus()
		return () => {
			if (previouslyFocusedRef.current instanceof HTMLElement) {
				previouslyFocusedRef.current.focus()
			}
		}
	}, [])

	useEffect(() => {
		if (typeof window === "undefined") return

		const scrollY = window.scrollY
		const body = document.body
		const html = document.documentElement
		const prev = {
			bodyOverflow: body.style.overflow,
			bodyPosition: body.style.position,
			bodyTop: body.style.top,
			bodyWidth: body.style.width,
			htmlOverflow: html.style.overflow,
		}

		body.style.overflow = "hidden"
		body.style.position = "fixed"
		body.style.top = `-${scrollY}px`
		body.style.width = "100%"
		html.style.overflow = "hidden"

		return () => {
			body.style.overflow = prev.bodyOverflow
			body.style.position = prev.bodyPosition
			body.style.top = prev.bodyTop
			body.style.width = prev.bodyWidth
			html.style.overflow = prev.htmlOverflow
			window.scrollTo(0, scrollY)
		}
	}, [])

	return (
		<motion.div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
			onClick={onClose}
			onKeyDown={(e) => e.key === "Escape" && onClose()}
			role="dialog"
			aria-modal="true"
			aria-label="Просмотр изображения"
			initial={{ opacity: 0, scale: 0.96 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.96 }}
			transition={{ duration: 0.15, ease: "easeOut" }}
		>
			<Button
				ref={closeButtonRef}
				type="button"
				variant="ghost"
				size="icon"
				aria-label="Закрыть"
				className="absolute right-4 top-[calc(var(--app-body-padding-top,0px)+1rem)] h-auto w-auto text-white hover:bg-transparent hover:text-gray-300"
				onClick={onClose}
			>
				<X className="h-8 w-8" />
			</Button>
			<img
				src={imageUrl}
				alt="Полноразмерное изображение"
				className="max-h-full max-w-full object-contain select-none"
				onContextMenu={(e) => e.preventDefault()}
				onDragStart={(e) => e.preventDefault()}
				onClick={(e) => e.stopPropagation()}
				onDoubleClick={(e) => {
					e.stopPropagation()
					setZoom((prev) => {
						const next = prev === 1 ? 2 : 1
						if (next === 1) setPan({ x: 0, y: 0 })
						return next
					})
				}}
				onWheel={(e) => {
					e.stopPropagation()
					e.preventDefault()
					const delta = -e.deltaY
					setZoom((prev) => {
						const step = Math.min(Math.abs(delta) / 600, 0.35)
						const next = delta > 0 ? prev * (1 + step) : prev * (1 - step)
						return Math.min(3, Math.max(1, next))
					})
				}}
				onPointerDown={(e) => {
					e.stopPropagation()
					;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
					pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

					if (pointersRef.current.size === 1 && zoom > 1) {
						dragRef.current = {
							startX: e.clientX,
							startY: e.clientY,
							startPanX: pan.x,
							startPanY: pan.y,
						}
					}

					if (pointersRef.current.size === 2) {
						const [a, b] = Array.from(pointersRef.current.values())
						const dx = a.x - b.x
						const dy = a.y - b.y
						pinchRef.current = {
							startDistance: Math.hypot(dx, dy),
							startZoom: zoom,
						}
						dragRef.current = null
					}
				}}
				onPointerMove={(e) => {
					if (!pointersRef.current.has(e.pointerId)) return
					pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

					if (pointersRef.current.size === 2 && pinchRef.current) {
						const [a, b] = Array.from(pointersRef.current.values())
						const dx = a.x - b.x
						const dy = a.y - b.y
						const dist = Math.hypot(dx, dy)
						const ratio = dist / pinchRef.current.startDistance
						const nextZoom = Math.min(3, Math.max(1, pinchRef.current.startZoom * ratio))
						setZoom(nextZoom)
						if (nextZoom === 1) setPan({ x: 0, y: 0 })
						return
					}

					if (zoom > 1 && dragRef.current) {
						const dx = e.clientX - dragRef.current.startX
						const dy = e.clientY - dragRef.current.startY
						setPan({
							x: dragRef.current.startPanX + dx,
							y: dragRef.current.startPanY + dy,
						})
					}
				}}
				onPointerUp={(e) => {
					pointersRef.current.delete(e.pointerId)
					dragRef.current = null
					if (pointersRef.current.size < 2) pinchRef.current = null
				}}
				onPointerCancel={(e) => {
					pointersRef.current.delete(e.pointerId)
					dragRef.current = null
					if (pointersRef.current.size < 2) pinchRef.current = null
				}}
				onKeyDown={(e) => e.key === "Escape" && onClose()}
				style={{
					transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
					transformOrigin: "center center",
					touchAction: "pinch-zoom",
					cursor: zoom > 1 ? "grab" : "zoom-in",
					transition: dragRef.current || pinchRef.current ? "none" : "transform 120ms ease-out",
				}}
			/>
		</motion.div>
	)
}
